"use client";

import Lenis from "lenis";
import { useEffect, useState } from "react";

// IMPORTANT: this provider's JSX tree MUST stay stable across all renders.
// The first attempt (`if (!enabled) return <>{children}</>; else return
// <ReactLenis>{children}</ReactLenis>;`) switched the wrapper type as soon
// as the post-mount useEffect detected the viewport, which React reconciled
// as "tree shape changed" → it unmounted every child including the
// <KessemLoader/>. The user saw the loader play, reset to 0 %, and play
// a second time before the site appeared.
//
// Fix: render a stable fragment with a side-effect-only `<LenisRunner/>`
// sibling that owns the Lenis instance imperatively. Flipping `enabled`
// no longer touches the children tree.

function LenisRunner({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled) return;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.4,
      wheelMultiplier: 1,
      lerp: 0.1,
    });
    let rafId = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [enabled]);
  return null;
}

export function LenisProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const update = () => {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const small = window.innerWidth < 768;
      setEnabled(!reduced && !small);
    };
    update();
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    window.addEventListener("resize", update);
    mql.addEventListener("change", update);
    return () => {
      window.removeEventListener("resize", update);
      mql.removeEventListener("change", update);
    };
  }, []);

  return (
    <>
      <LenisRunner enabled={enabled} />
      {children}
    </>
  );
}
